import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService, PagedRequest, PagedResponse } from '../base/base-api.service';
import type { Lease } from './lease.service';
import type { Invoice, CustomerInvoice } from './invoice.service';
import type { Vessel } from './vessel.service';
import type { StripeCustomerDto, StripePaymentMethodDto } from './stripe.service';
import type { LaunchDto } from './launch.service';

/** Staff portal: customer + vessels + leases + invoices in one call. */
export interface StaffCustomerRecordBundle {
  customer: Customer;
  vessels: Vessel[];
  leases: Lease[];
  invoices: Invoice[];
}

/** Staff portal: shell load — customer + tab badge counts only. */
export interface StaffCustomerSummary {
  customer: Customer;
  vesselCount: number;
  leaseCount: number;
  invoiceCount: number;
  insuranceRecordCount: number;
  noteCount: number;
}

/** Mirrors audit trail rows returned under staff-tab/audit (camelCase from API). */
export interface StaffCustomerAuditTrailDto {
  id: string;
  userId?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  userRole?: string | null;
  entity: string;
  entityId: string;
  action: string;
  oldValue?: string | null;
  newValue?: string | null;
  details?: string | null;
  timestamp: string;
  ipAddress?: string | null;
}

/** Single-tab payload from GET customers/{id}/staff-tab/{tab}. */
export interface StaffCustomerTabPayload {
  vessels?: Vessel[];
  leases?: Lease[];
  invoices?: Invoice[];
  insuranceRecords?: import('./compliance.service').InsuranceDto[];
  auditTrails?: StaffCustomerAuditTrailDto[];
}

/** Aggregated dashboard data returned by a single API call. */
export interface CustomerDashboard {
  customer: Customer;
  vessels: Vessel[];
  contracts: Lease[];
  unpaidInvoices: CustomerInvoice[];
  paymentMethod: StripeCustomerDto | null;
  launchRequests: LaunchDto[];
  /** Owner | Admin | Dockhand (legacy API value: Captain); omitted for legacy responses. */
  portalRole?: string | null;
  /** When portalRole is Dockhand, vessel IDs this user may access (empty = none). API field: captainVesselIds. */
  captainVesselIds?: string[] | null;
  /** Personal name for the signed-in portal user (dashboard greeting). */
  portalUserFirstName?: string | null;
  portalUserLastName?: string | null;
}

/** Staff read-only: Stripe summary + saved payment methods (matches CustomerPaymentMethodsInfoDto). */
export interface CustomerPaymentMethodsInfo {
  summary: StripeCustomerDto | null;
  paymentMethods: StripePaymentMethodDto[];
}

export interface CustomerSetupIntentResponse {
  clientSecret: string;
  setupIntentId?: string;
}

export interface AuthorizedUser {
  id: string;
  customerId: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  /** Optional job title */
  title?: string | null;
  /** Phone number */
  phoneNumber?: string | null;
  /** Role: Owner | Admin | Dockhand (legacy: Captain) */
  role: 'Owner' | 'Admin' | 'Dockhand' | 'Captain' | string;
  /** Status: Active | PendingActivation */
  status: 'Active' | 'PendingActivation' | string;
  /** Vessel IDs assigned to this user (Dockhand only) */
  vesselScope: string[];
  /** Boat Club Dockhand: all customer boats (current and future) */
  allVessels?: boolean;
  /** When true, may charge purchases to the account owner's saved payment method */
  canChargeOwnerPaymentMethod?: boolean;
  createdAt: string;
  /** Sent | Failed | Skipped — present when user was just added (S9.2). */
  invitationEmailStatus?: 'Sent' | 'Failed' | 'Skipped' | string;
}

export interface AddAuthorizedUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  /** Optional job title */
  title?: string | null;
  /** Required phone number */
  phoneNumber: string;
  /** "Admin" or "Dockhand" (legacy: "Captain"). "Owner" is not allowed here. */
  role: string;
  /** Required when role is Dockhand unless allVessels is true */
  vesselIds?: string[];
  /** Boat Club Dockhand: bind all current and future customer vessels */
  allVessels?: boolean;
  /** When true, may charge purchases to the account owner's saved payment method */
  canChargeOwnerPaymentMethod?: boolean;
}

/** S4.4 — update role and/or Dockhand boat assignments */
export interface UpdateAuthorizedUserRequest {
  /** "Admin" or "Dockhand" (legacy: "Captain") */
  role: string;
  /** Required when role is Dockhand unless allVessels is true */
  vesselIds?: string[];
  /** Boat Club Dockhand: bind all current and future customer vessels */
  allVessels?: boolean;
  /** When true, may charge purchases to the account owner's saved payment method */
  canChargeOwnerPaymentMethod?: boolean;
}

/** Primary Owner → Active Admin ownership handover */
export interface TransferOwnershipRequest {
  newOwnerAuthorizedUserEntryId: string;
}

/** Staff read-only: Stripe summary + saved payment methods (matches CustomerPaymentMethodsInfoDto). */
export interface CustomerPaymentMethodsInfo {
  summary: StripeCustomerDto | null;
  paymentMethods: StripePaymentMethodDto[];
}

export interface CustomerSetupIntentResponse {
  clientSecret: string;
  setupIntentId?: string;
}

// These types should match the models from @marina/models
// For now, defining them here - they'll be moved to models library later
export interface Customer {
  id: string;
  marinaId?: string | null;
  /** Account type: PERSONAL, BUSINESS, BOAT_CLUB */
  customerType?: string;
  firstName: string;
  lastName: string;
  businessName?: string;
  email: string;
  emailSecondary?: string;
  phoneMobile?: string;
  phoneHome?: string;
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactCell?: string;
  emergencyContactRelationship?: string;
  emergencyContactEmail?: string;
  emergencyContactAddress?: string;
  authorizedUsersJson?: string; // JSON string storing authorized users data
  /** Client status derived from contracts. System-managed (Prospective, Contracted, Terminated). */
  clientStatus?: string;
  isActive: boolean;
  /** True when primary login has set a password (EmailConfirmed). */
  isLoginActivated?: boolean;
  isOnboardingRequired: boolean;
  isPaymentReminderEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomerRequest {
  marinaId?: string; // Optional - will be set automatically from HttpContext on backend
  /** Account type: PERSONAL, BUSINESS, BOAT_CLUB */
  customerType?: string;
  firstName: string;
  lastName: string;
  businessName?: string;
  email: string;
  emailSecondary?: string;
  phoneMobile?: string;
  phoneHome?: string;
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactCell?: string;
  emergencyContactRelationship?: string;
  emergencyContactEmail?: string;
  emergencyContactAddress?: string;
  authorizedUsersJson?: string; // JSON string storing authorized users data
  isActive?: boolean;
  isPaymentReminderEnabled?: boolean;
  /** When false, create pending login without sending activation invite. Default true on API. */
  /** Always sent as true on create; invite is automatic. Kept for API compatibility. */
  sendActivationEmail?: boolean;
}

export interface UpdateCustomerRequest {
  /** Account type: PERSONAL, BUSINESS, BOAT_CLUB */
  customerType?: string;
  firstName?: string;
  lastName?: string;
  businessName?: string | null;
  emailSecondary?: string | null;
  phoneMobile?: string;
  phoneHome?: string;
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactCell?: string | null;
  emergencyContactRelationship?: string;
  emergencyContactEmail?: string;
  emergencyContactAddress?: string;
  authorizedUsersJson?: string; // JSON string storing authorized users data
  isActive?: boolean;
  isOnboardingRequired?: boolean; // Optional - can be set to false to mark onboarding as complete
  isPaymentReminderEnabled?: boolean;
}

export interface CustomerFilterRequest extends PagedRequest {
  customerType?: string; // ALL, BUSINESS, PERSONAL, BOAT_CLUB
  /** Multi-select filter (preferred when present). */
  customerTypes?: string[]; // e.g. ['PERSONAL','BOAT_CLUB']
  status?: string; // ALL, ACTIVE, INACTIVE
}

@Injectable({
  providedIn: 'root',
})
export class CustomerService extends BaseApiService {
  constructor(http: HttpClient) {
    super(http);
  }
  /**
   * Get customer by ID
   */
  getById(id: string): Observable<Customer> {
    return this.get<Customer>(`customers/${id}`);
  }

  /**
   * Get all customers with pagination
   */
  getAll(request?: PagedRequest): Observable<PagedResponse<Customer>> {
    return this.getPaged<Customer>('customers', request);
  }

  /**
   * Get all customers with filtering and sorting
   */
  getAllFiltered(request?: CustomerFilterRequest): Observable<PagedResponse<Customer>> {
    // Build params including customerType and status
    let params = this.getPagedParams(request);

    if (request?.customerTypes?.length) {
      // Repeat query keys for ASP.NET Core model binding (List<string>)
      request.customerTypes.forEach((t) => {
        params = params.append('customerTypes', t);
      });
    } else
    if (request?.customerType) {
      params = params.set('customerType', request.customerType);
    }
    if (request?.status) {
      params = params.set('status', request.status);
    }

    return this.get<PagedResponse<Customer>>('customers/filtered', params);
  }

  /**
   * Get customer by email
   */
  getByEmail(email: string): Observable<Customer> {
    return this.get<Customer>(`customers/by-email?email=${encodeURIComponent(email)}`);
  }

  /**
   * Get current logged-in customer's profile
   */
  getMyProfile(): Observable<Customer> {
    return this.get<Customer>('customers/me');
  }

  /**
   * Staff: customer record bundle for portal client popup (single HTTP request).
   */
  getStaffRecordBundle(customerId: string): Observable<StaffCustomerRecordBundle> {
    return this.get<StaffCustomerRecordBundle>(`customers/${customerId}/staff-record`);
  }

  /** Staff: customer + tab counts only (portal shell). */
  getStaffSummary(customerId: string): Observable<StaffCustomerSummary> {
    return this.get<StaffCustomerSummary>(`customers/${customerId}/staff-summary`);
  }

  /** Staff: one tab slice (overview | vessels | contracts | invoices | insurance | audit). */
  getStaffTab(customerId: string, tab: string): Observable<StaffCustomerTabPayload> {
    return this.get<StaffCustomerTabPayload>(
      `customers/${customerId}/staff-tab/${encodeURIComponent(tab)}`
    );
  }

  /**
   * Get aggregated dashboard data (profile, vessels, contracts, unpaid invoices, payment method, launch requests).
   * Single API call to reduce round trips and improve performance.
   */
  getDashboard(): Observable<CustomerDashboard> {
    return this.get<CustomerDashboard>('customers/dashboard');
  }

  /**
   * Staff read-only: payment methods on file and AutoPay flags for a customer.
   */
  getPaymentMethodsInfo(customerId: string): Observable<CustomerPaymentMethodsInfo> {
    return this.get<CustomerPaymentMethodsInfo>(`customers/${customerId}/payment-methods-info`);
  }

  /** Staff/admin: create SetupIntent for a specific customer. */
  createCustomerSetupIntent(customerId: string, request?: { paymentMethodType?: 'card' | 'bank_account' }): Observable<CustomerSetupIntentResponse> {
    return this.post<CustomerSetupIntentResponse>(`customers/${customerId}/payment-methods/setup-intent`, request ?? {});
  }

  /** Staff/admin: confirm SetupIntent for a specific customer (marks method as admin-added). */
  confirmCustomerSetupIntent(
    customerId: string,
    payload: {
      setupIntentId: string;
      isAutoPayEnabled: boolean;
      autoPayAuthDocUrl?: string;
      replacePaymentMethodId?: string;
      makeDefault: boolean;
    }
  ): Observable<StripeCustomerDto> {
    return this.post<StripeCustomerDto>(`customers/${customerId}/payment-methods/confirm-setup-intent`, payload);
  }

  /** Staff/admin: delete only admin-added payment methods for a customer. */
  removeCustomerPaymentMethod(customerId: string, paymentMethodId: string): Observable<void> {
    return this.deleteRequest<void>(`customers/${customerId}/payment-methods/${encodeURIComponent(paymentMethodId)}`);
  }

  /**
   * Create new customer
   */
  create(request: CreateCustomerRequest): Observable<Customer> {
    return this.post<Customer>('customers', request);
  }

  /**
   * Update customer
   */
  update(id: string, request: UpdateCustomerRequest): Observable<Customer> {
    return this.put<Customer>(`customers/${id}`, request);
  }

  /**
   * Change primary login email (resets activation, sends invite, forces onboarding).
   */
  updatePrimaryEmail(id: string, email: string): Observable<Customer> {
    return this.patch<Customer>(`customers/${id}/primary-email`, { email });
  }

  /**
   * Delete customer
   */
  deleteCustomer(id: string): Observable<void> {
    return this.deleteRequest(`customers/${id}`);
  }

  // -------------------------------------------------------------------------
  // Authorized Users (S4.1)
  // -------------------------------------------------------------------------

  getAuthorizedUsers(customerId: string): Observable<AuthorizedUser[]> {
    return this.get<AuthorizedUser[]>(`customers/${customerId}/authorized-users`);
  }

  addAuthorizedUser(customerId: string, request: AddAuthorizedUserRequest): Observable<AuthorizedUser> {
    return this.post<AuthorizedUser>(`customers/${customerId}/authorized-users`, request);
  }

  updateAuthorizedUser(
    customerId: string,
    entryId: string,
    request: UpdateAuthorizedUserRequest
  ): Observable<AuthorizedUser> {
    return this.put<AuthorizedUser>(`customers/${customerId}/authorized-users/${entryId}`, request);
  }

  removeAuthorizedUser(customerId: string, entryId: string): Observable<void> {
    return this.deleteRequest<void>(`customers/${customerId}/authorized-users/${entryId}`);
  }

  transferOwnership(customerId: string, request: TransferOwnershipRequest): Observable<void> {
    return this.post<void>(`customers/${customerId}/transfer-ownership`, request);
  }

  /**
   * Send confirmation email to customer
   */
  sendConfirmationEmail(id: string): Observable<void> {
    return this.post<void>(`customers/${id}/send-confirmation-email`, {});
  }

  /**
   * One-time backfill: sync client and vessel statuses from existing contracts.
   * Use after migration to fix customers showing Prospective despite having active contracts.
   */
  syncClientVesselStatuses(): Observable<{ message: string }> {
    return this.post<{ message: string }>('customers/sync-statuses', {});
  }

}

